import { ProductStockInfo, ProductsTableItem } from "../../../modules/merchant/admin/products/productsTypes"

type StockCheckResult = Readonly<{
    allowed: boolean
    maxQuantity: number | undefined
    reason: "daily-limit" | "stock-limit" | null
}>

export function checkProductStock(stock: ProductStockInfo | undefined, quantity: number): StockCheckResult {
    // 1. Check daily items for sale limit (only if itemsForSale is configured)
    const daily = stock?.sales?.daily
    const dailyRemaining = daily?.itemsForSale ? daily.remaining : undefined
    if (dailyRemaining !== undefined && quantity > dailyRemaining) {
        return { allowed: false, maxQuantity: dailyRemaining, reason: "daily-limit" }
    }

    // 2. Check stock inventory available (fall back to total if available is not provided)
    const inv = stock?.inventory
    const inventoryAvailable = inv !== undefined ? (inv.available ?? inv.total) : undefined
    if (inventoryAvailable !== undefined && quantity > inventoryAvailable) {
        return { allowed: false, maxQuantity: inventoryAvailable, reason: "stock-limit" }
    }

    return { allowed: true, maxQuantity: undefined, reason: null }
}

export function getMaxQuantity(stock: ProductStockInfo | undefined): number | undefined {
    const limits: number[] = []

    const daily = stock?.sales?.daily
    if (daily?.itemsForSale) limits.push(daily.remaining)

    const inv = stock?.inventory
    const inventoryAvailable = inv !== undefined ? (inv.available ?? inv.total) : undefined
    if (inventoryAvailable !== undefined) limits.push(inventoryAvailable)

    return limits.length > 0 ? Math.min(...limits) : undefined
}

export function getProductLeftCount(stock: ProductStockInfo | undefined): number | undefined {
    const daily = stock?.sales?.daily
    if (!daily || !daily.itemsForSale) return undefined
    return daily.remaining
}

export function isAllStockReached(
    products: ProductsTableItem[],
    orderLines: readonly { id: string | number; quantity: number; isExtraService?: boolean }[],
): boolean {
    const availableWithStock = products.filter((p) => {
        const isUnavailable = p.orderDetails?.availability !== undefined && !p.orderDetails.availability.isAvailable
        return p.stock !== undefined && !isUnavailable
    })

    if (availableWithStock.length === 0) return false

    return availableWithStock.every((p) => {
        const maxQty = getMaxQuantity(p.stock)
        if (maxQty === undefined) return false
        const cartItem = orderLines.find((item) => item.id === p.id && !item.isExtraService)
        return (cartItem?.quantity ?? 0) >= maxQty
    })
}
